import React, { useEffect, useState } from 'react';
import { alpha, Box, Button, Chip, Stack, Theme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { ElementClarifyingQuestion, QuestionStatus } from 'api/element-issues';
import useBundleTranslation from 'i18n';
import IssueCard, { IssueCardRows } from 'components/common/issue-card/IssueCard';
import { IssueObjectType } from 'app/extreport/concierge/InconsistenciesCard';
import useForceExpand from 'hooks/useForceExpand';

function renderQuestionText(text: string): React.ReactNode {
    const parts = text.split(/(`[^`]+`)/g);
    return parts.map((part, i) => {
        if (part.startsWith('`') && part.endsWith('`')) {
            return <Chip label={part.slice(1, -1)} className={'chip-mod--round-and-text'} sx={{ height: '18px' }} />;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

const STATUS_COLOR_ALIAS = {
    Unanswered: 'default',
    Skipped: 'default',
    Answered: 'success',
};

interface Props {
    questions: ElementClarifyingQuestion[];
    onSkip: (questionId: string) => Promise<void>;
    onUndo: (questionId: string) => void;
    onSkipAll: () => Promise<void>;
    onOpenConcierge: (questionId?: string) => void;
    // Auto-expand on gating errors (§2.2): re-expands whenever the token changes
    forceExpandToken?: number;
    objectType?: IssueObjectType;
}

export default function ClarificationsCard({
    questions,
    onSkip,
    onUndo,
    onSkipAll,
    onOpenConcierge,
    forceExpandToken,
    objectType = 'report',
}: Props) {
    const { t } = useBundleTranslation('app/content-workflow/element-editor');
    const [expanded, setExpanded] = useState(true);

    const allDone = questions.every((q) => q.status !== 'Unanswered');
    const unansweredCount = questions.filter((q) => q.status === 'Unanswered').length;

    useEffect(() => {
        if (allDone) setExpanded(false);
    }, [allDone]);

    useForceExpand(forceExpandToken, setExpanded);

    const badge = allDone ? (
        <Chip label={t('all_done')} color="success" className={'chip-mod--round-and-text'} />
    ) : (
        <Chip
            label={t('unanswered_count', { count: unansweredCount })}
            color="primary"
            className={'chip-mod--round-and-text'}
        />
    );

    return (
        <IssueCard
            color="primary"
            title={t('clarifying_questions')}
            badge={badge}
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
        >
            <Box sx={{ pb: 1, fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1) }}>
                {t('subtitle', { objectType: t(`object_type_${objectType}`) })}
            </Box>

            <IssueCardRows>
                {questions.map((q, idx) => (
                    <Stack
                        key={q.questionId}
                        direction="row"
                        alignItems="flex-start"
                        spacing={2}
                        sx={{
                            px: 1,
                            py: '6px',
                            borderTop: idx > 0 ? '1px solid' : 'none',
                            borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                            '&:hover .skip-btn': { visibility: 'visible' },
                        }}
                    >
                        <Stack
                            flexGrow={1}
                            spacing={0.25}
                            onClick={q.status !== 'Skipped' ? () => onOpenConcierge(q.questionId) : undefined}
                            sx={{ pt: '1px', ...(q.status !== 'Skipped' && { cursor: 'pointer' }) }}
                        >
                            <Box
                                sx={{
                                    '&:hover':
                                        q.status !== 'Skipped'
                                            ? { textDecoration: 'underline', color: 'primary.main' }
                                            : {},
                                    opacity: q.status === 'Skipped' ? 0.6 : undefined,
                                }}
                            >
                                {renderQuestionText(q.question)}
                            </Box>
                            {q.status === 'Answered' && q.answer && (
                                <Box sx={{ color: 'success.main' }}>
                                    {'↳ '}
                                    {q.answer}
                                </Box>
                            )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
                            <Chip
                                label={t(`status_${q.status.toLowerCase()}`)}
                                color={STATUS_COLOR_ALIAS[q.status] as any}
                                className={'chip-mod--round-and-text'}
                                sx={{ opacity: q.status === 'Skipped' ? 0.6 : undefined }}
                            />

                            {q.status === 'Unanswered' && (
                                <Box sx={{ minWidth: '40px' }}>
                                    <Box
                                        className="skip-btn default-link"
                                        onClick={() => onSkip(q.questionId)}
                                        sx={{
                                            userSelect: 'none',
                                            visibility: 'hidden',
                                        }}
                                    >
                                        {t('skip')}
                                    </Box>
                                </Box>
                            )}
                            {q.status === 'Skipped' && (
                                <Box sx={{ minWidth: '40px' }}>
                                    <Box
                                        className={'default-link'}
                                        onClick={() => onUndo(q.questionId)}
                                        sx={{ userSelect: 'none' }}
                                    >
                                        {t('undo')}
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                ))}
            </IssueCardRows>

            {!allDone && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Button variant="text" onClick={onSkipAll} sx={{ px: 0.5, minWidth: 0 }}>
                        {t('skip_all')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => onOpenConcierge(questions.find((q) => q.status === 'Unanswered')?.questionId)}
                        endIcon={<IconMi icon="arrow-right" />}
                    >
                        {t('answer_in_concierge')}
                    </Button>
                </Stack>
            )}
        </IssueCard>
    );
}
